// Mirrors backend/utils/passwordPolicy.js - keep the two in sync.
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const PASSWORD_HISTORY_LIMIT = 5;

// `label` is the checklist wording, `requirement` the clause used when the
// unmet rules are joined into one sentence.
export const PASSWORD_RULES = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    requirement: `at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "uppercase",
    label: "One uppercase letter (A-Z)",
    requirement: "an uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: "lowercase",
    label: "One lowercase letter (a-z)",
    requirement: "a lowercase letter",
    test: (value) => /[a-z]/.test(value),
  },
  {
    id: "number",
    label: "One number (0-9)",
    requirement: "a number",
    test: (value) => /[0-9]/.test(value),
  },
  {
    id: "symbol",
    label: "One special symbol (!@#$...)",
    requirement: "a special symbol",
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
];

export const ruleResults = (password = "") =>
  PASSWORD_RULES.map((rule) => ({
    id: rule.id,
    label: rule.label,
    met: rule.test(password),
  }));

export const isPasswordCompliant = (password = "") =>
  password.length <= PASSWORD_MAX_LENGTH &&
  PASSWORD_RULES.every((rule) => rule.test(password));

export const passwordPolicyMessage = (password = "") => {
  if (!password) return "Password is required.";
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`;
  }

  const failed = PASSWORD_RULES.filter((rule) => !rule.test(password));
  if (!failed.length) return "";

  return `Password must have ${failed.map((rule) => rule.requirement).join(", ")}.`;
};

export const STRENGTH_LEVELS = [
  { label: "Very weak", barClass: "bg-danger-500", textClass: "text-danger-600" },
  { label: "Weak", barClass: "bg-danger-400", textClass: "text-danger-600" },
  { label: "Fair", barClass: "bg-warning-500", textClass: "text-warning-700" },
  { label: "Strong", barClass: "bg-success-500", textClass: "text-success-700" },
  { label: "Very strong", barClass: "bg-success-600", textClass: "text-success-800" },
];

const SEQUENCES = ["abcdefghijklmnopqrstuvwxyz", "01234567890", "qwertyuiop"];

// Heuristic score from 0-4. Character variety and length earn points; patterns
// that make a password easy to guess take them away.
export const scorePassword = (password = "") => {
  if (!password) return 0;

  const lower = password.toLowerCase();
  let score = 0;

  if (password.length >= 8) score += 1;
  if (password.length >= PASSWORD_MIN_LENGTH) score += 1;
  if (password.length >= 16) score += 1;

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) =>
    re.test(password)
  ).length;
  if (classes >= 3) score += 1;
  if (classes === 4) score += 1;

  // Unique characters matter more than raw length for guess resistance.
  if (new Set(password).size >= password.length * 0.7) score += 1;

  if (/(.)\1{2,}/.test(password)) score -= 1;

  const hasSequence = SEQUENCES.some((seq) => {
    for (let i = 0; i + 4 <= seq.length; i += 1) {
      const chunk = seq.slice(i, i + 4);
      if (lower.includes(chunk) || lower.includes([...chunk].reverse().join(""))) {
        return true;
      }
    }
    return false;
  });
  if (hasSequence) score -= 1;

  if (/^(password|admin|welcome|letmein|qwerty)/i.test(password)) score -= 2;

  return Math.max(0, Math.min(4, score - 2));
};

export const strengthFor = (password = "") => {
  const score = scorePassword(password);
  return { score, ...STRENGTH_LEVELS[score] };
};
